import { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { usePmProperty } from '@/hooks/usePmProperty';
import { getPmIncidents } from '@/services/api.client';
import { IncidentDetailPanel } from '@/components/pm/IncidentDetailPanel';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { IncidentStatus, IncidentSummary } from '@/types';
import { PaginationControls } from '@/components/ui/pagination-controls';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const STATUS_VARIANTS: Record<IncidentStatus, 'default' | 'secondary' | 'destructive' | 'outline'> =
  {
    open: 'destructive',
    under_review: 'secondary',
    closed: 'outline',
  };

export function IncidentsPage() {
  const pm = usePmProperty();
  const propertyId = pm?.propertyId ?? '';
  const [statusFilter, setStatusFilter] = useState<IncidentStatus | ''>('');
  const [selected, setSelected] = useState<IncidentSummary | null>(null);
  const [page, setPage] = useState(1);

  const query = useQuery({
    queryKey: ['pm', 'incidents', propertyId, statusFilter, page],
    queryFn: () =>
      getPmIncidents(propertyId, {
        ...(statusFilter ? { status: statusFilter } : {}),
        page,
        perPage: 20,
      }),
    enabled: !!propertyId,
    placeholderData: keepPreviousData,
  });

  const incidents = query.data?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl">Incidents</h1>
        <Select
          value={statusFilter || '__all__'}
          onValueChange={(v) => {
            setStatusFilter(v === '__all__' ? '' : (v as IncidentStatus));
            setPage(1);
          }}
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All statuses</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="under_review">Under Review</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Reference</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Logged at</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isError ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <div
                    role="alert"
                    className="rounded-xl border border-destructive/40 bg-destructive/5 p-4 my-2"
                  >
                    <p className="text-sm font-medium">Couldn&apos;t load incidents</p>
                    <p className="text-sm text-muted-foreground">Please try again later.</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : query.isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 5 }).map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : incidents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-[var(--text-tertiary)] py-10">
                  No incidents logged yet.
                </TableCell>
              </TableRow>
            ) : (
              incidents.map((inc) => (
                <TableRow key={inc.id} onClick={() => setSelected(inc)} className="cursor-pointer">
                  <TableCell className="font-medium">{inc.referenceNumber}</TableCell>
                  <TableCell>{inc.title}</TableCell>
                  <TableCell className="capitalize">
                    {inc.incidentType.replace(/_/g, ' ')}
                  </TableCell>
                  <TableCell>
                    <Badge variant={STATUS_VARIANTS[inc.status]} className="text-xs">
                      {inc.status.replace('_', ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-[var(--text-tertiary)]">
                    {new Date(inc.createdAt).toLocaleString()}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <PaginationControls
        currentPage={page}
        totalPages={query.data?.meta?.totalPages ?? 1}
        onPageChange={setPage}
        disabled={query.isPlaceholderData}
      />

      <IncidentDetailPanel
        incident={selected}
        propertyId={propertyId}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}
